"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const axios_1 = require("axios");
const lodash_1 = require("lodash");
const errors_1 = require("./errors");
const util_1 = require("./util");
class BaseApiClient {
    constructor(config) {
        if (!config) {
            throw new Error('Must provide the client configuration.');
        }
        const { clientApplicationName, clientApplicationVersion, url, authToken, apiKey, additionalHeaders, httpsAgent, skipUndefined, } = config;
        if (!clientApplicationName) {
            throw new Error('Must provide a client application name.');
        }
        if (!url) {
            throw new Error('Must provide an API URL.');
        }
        this.url = url;
        this.defaultHeaders = {
            'X-Api-Client': clientApplicationName,
            ...(!!clientApplicationVersion ? { 'X-Api-Client-Version': clientApplicationVersion } : {}),
            'Content-Type': 'application/json',
            ...(!!authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            ...(!!apiKey ? { 'X-Api-Key': apiKey } : {}),
            ...additionalHeaders,
        };
        this.httpsAgent = httpsAgent;
        this.skipUndefined = skipUndefined !== null && skipUndefined !== void 0 ? skipUndefined : false;
    }
    isPagination(obj) {
        return Object.keys(obj).every((key) => ['limit', 'offset', 'sort', 'direction'].includes(key));
    }
    mapResponseError({ response, message, request, code }) {
        if (response) {
            throw new errors_1.ApiClientResponseError(response.status, response.data, `Received error from API at ${this.url}: ${message} with body ${JSON.stringify(response.data)}`);
        }
        else {
            throw new errors_1.ApiClientUncaughtError(`Error in client when calling API at ${this.url}: ${JSON.stringify(message)}`, { message, request, code, response });
        }
    }
    async get(paramA, paramB = {}, paramC = {}, options) {
        const path = paramA;
        let filters, pagination;
        // Test whether paramB is filters or pagination
        if (!this.isPagination(paramB) || Object.keys(paramC).length > 0) {
            filters = paramB;
            pagination = pagination = util_1.validatePagination(paramC);
        }
        else {
            pagination = util_1.validatePagination(paramB);
        }
        return await this.getAxiosResponse('GET', this.formatUrl({ path, params: { ...filters, ...pagination } }), options);
    }
    async getAllPages(path, filters = {}, pagination = { limit: 100 }, options) {
        const result = [];
        let getNext = true;
        let offset = pagination.offset || 0; // tslint:disable-line:strict-boolean-expressions
        while (getNext) {
            const page = await this.get(path, filters, { ...pagination, offset }, options);
            result.push(...page);
            offset += pagination.limit;
            if (page.length < pagination.limit) {
                getNext = false;
            }
        }
        return result;
    }
    async post(path, obj, options) {
        return await this.getAxiosResponse('POST', this.formatUrl({ path }), obj, options);
    }
    async put(path, obj, options) {
        return await this.getAxiosResponse('PUT', this.formatUrl({ path }), obj, options);
    }
    async patch(path, obj, filters, options) {
        return await this.getAxiosResponse('PATCH', this.formatUrl({ path, params: filters }), obj, options);
    }
    async delete(path, options) {
        return await this.getAxiosResponse('DELETE', this.formatUrl({ path }), options);
    }
    async health(options) {
        return await this.getAxiosResponse('GET', this.formatUrl({ path: '/health' }), options);
    }
    formatUrl({ path, params }) {
        var _a;
        // tslint:disable-next-line: strict-type-predicates
        const baseUrl = new URL(this.url, typeof window !== 'undefined' ? (_a = window.location) === null || _a === void 0 ? void 0 : _a.origin : undefined);
        const url = new URL([baseUrl.pathname, path].join('/').replace(/\/+/g, '/'), baseUrl);
        if (params !== undefined) {
            Object.entries(params).forEach(([name, value]) => {
                if (value !== undefined || !this.skipUndefined) {
                    for (const item of lodash_1.castArray(value)) {
                        url.searchParams.append(name, String(item !== null && item !== void 0 ? item : ''));
                    }
                }
            });
        }
        return url.toString();
    }
    async getAxiosResponse(method, url, data, { headers } = {}) {
        const config = {
            method,
            url,
            headers: { ...this.defaultHeaders, ...headers },
            httpsAgent: this.httpsAgent,
            data,
        };
        try {
            const response = await axios_1.default(config);
            return response.data;
        }
        catch (error) {
            throw this.mapResponseError(error);
        }
    }
}
exports.BaseApiClient = BaseApiClient;
